<script lang="ts">
  import { ConfirmActions, Title } from '$shared/components';
  import { FileAttachmentConflict } from '$shared/models/file/FileAttachmentConflict';
  import { FileGuardOperation } from '$shared/constants/FileGuardOperation';
  import ModalEvent from '$shared/constants/ModalEvent';
  import UIMessage from '$shared/constants/UIMessages';
  import Label from '$shared/constants/Label';
  import UIDisclaimer from '$provider/constants/UIDisclaimer';
  import { onMount } from 'svelte';

  interface Props {
    conflicts?: FileAttachmentConflict[];
    isFolder?: boolean;
    fileName?: string;
    operation?: FileGuardOperation;
    onevent?: (payload: any) => void;
  }

  let {
    conflicts = [],
    isFolder = false,
    fileName = '',
    operation = FileGuardOperation.Delete,
    onevent
  }: Props = $props();

  let title: string = $state('');
  let operationText: string = $state('');
  let warningMessage: string = $state('');
  let groupedConflicts: Map<string, FileAttachmentConflict[]> = $state(new Map());

  onMount(() => {
    title = isFolder
      ? UIMessage.FolderContainsAttachedFiles
      : UIMessage.FileAttachedToChecklistItems;

    operationText = operation === FileGuardOperation.Delete ? Label.Deleting : Label.Renaming;

    warningMessage =
      operation === FileGuardOperation.Delete
        ? UIDisclaimer.RemoveFileConfirmation
        : UIDisclaimer.RenameFileConfirmation;

    groupedConflicts = FileAttachmentConflict.groupByCredReq(conflicts);
  });

  const handleOk = () => {
    onevent?.({ type: ModalEvent.ok });
  };

  const handleCancel = () => {
    onevent?.({ type: ModalEvent.close });
  };
</script>

<div class="file-attachment-warning-popup">
  <div class="title">
    <Title type="h3">{title}</Title>
  </div>

  <div class="desc">
    <strong>{operationText} "{fileName}"</strong> will break references in the following checklist items:
  </div>

  <div class="conflicts-list">
    {#each Array.from(groupedConflicts.entries()) as [key, groupConflicts]}
      {@const firstConflict = groupConflicts?.first()}
      {@const uniqueChecklistItems = FileAttachmentConflict.getUniqueChecklistItems(groupConflicts)}
      <div class="request-group">
        <div class="request-name">
          • {firstConflict.credentialingRequestName} ({firstConflict.credentialingRequestType})
        </div>
        <ul class="checklist-items">
          {#each uniqueChecklistItems as checklistItemName}
            <li>{checklistItemName}</li>
          {/each}
        </ul>
      </div>
    {/each}
  </div>

  <div class="warning">
    {warningMessage}
  </div>

  <div class="actions">
    <ConfirmActions
      okLabel="Continue anyway"
      cancelLabel="Cancel"
      onok={handleOk}
      oncancel={handleCancel}
    />
  </div>
</div>

<style>
  .file-attachment-warning-popup {
    width: 500px;
    padding: 24px;
  }

  .title {
    margin-bottom: 16px;
  }

  .desc {
    font-size: 14px;
    line-height: 1.5;
    color: var(--grey-dark);
    margin-bottom: 16px;
  }

  .conflicts-list {
    margin-bottom: 16px;
    padding: 12px;
    background-color: var(--grey-bg);
    border-radius: 4px;
    max-height: 300px;
    overflow-y: auto;
  }

  .request-group {
    margin-bottom: 12px;
  }

  .request-group:last-child {
    margin-bottom: 0;
  }

  .request-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--grey-dark);
    margin-bottom: 6px;
  }

  .checklist-items {
    list-style: none;
    padding-left: 20px;
    margin: 0;
  }

  .checklist-items li {
    font-size: 13px;
    color: var(--grey-medium);
    margin-bottom: 4px;
    position: relative;
  }

  .checklist-items li:before {
    content: '- ';
    position: absolute;
    left: -12px;
  }

  .warning {
    font-size: 14px;
    line-height: 1.5;
    color: var(--grey-medium);
    margin-bottom: 20px;
    font-style: italic;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
